<template>
  <div>
    <booking-filters
      v-model="currentFilter"
      :loading="loading || Boolean(error)"
      :filters="availableFilters"
      :result-count="selectedRooms.length" />

    <booking-rooms
      :rooms="selectedRooms"
      :loading="loading || Boolean(error)"
      :user-currency="bookingStore.currentCurrency"
      :current-filter="currentFilter"
      @set-room="setRoom" />
  </div>
</template>

<script setup lang="ts">
import { usePreferentialCode } from '~/composables/useIdentificationPlaintext';

import { SESSION_STORAGE_KEYS } from '~~/core';
import {
  BookingFilterValues,
  BookingRateFilter,
  BookingRoomFilter,
  COOKIE_BASKET_ID,
  useAvailableRoomsSorted,
  useBookingStore,
} from '~~/domains/booking';
import { computeAdultsAndChildren } from '~~/domains/booking/helpers';
import type { AvailableRoom, Categories, RoomOffers } from '~~/domains/graphql';
import { useAvailableRoomsQuery } from '~~/domains/graphql';
import { RoomCategory } from '~~/domains/hotel';
import { ecommerce, event, pageView } from '~~/helpers/gtm.helper';

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/booking/[ridcode]/room',
    fr: '/reservation/[ridcode]/chambre',
    de: '/buchung/[ridcode]/schlafzimmer',
    ru: '/Бронирование/[ridcode]/cпальня',
    tr: '/rezervasyon/[ridcode]/yatak-odasi',
  },
});

pageView({
  page_category: 'booking - select room',
  page_name: 'booking::step1room',
  page_sub_category_level_1: 'booking',
  page_sub_category_level_2: 'step1room',
});

type Props = {
  roomError?: boolean;
};

type Emits = {
  (event: 'submit', step: 'room'): void;
  (event: 'error', error: Error): void;
  (event: 'update:room-error', error: boolean): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const bookingStore = useBookingStore();
const preferentialCode = usePreferentialCode();
const { derbySoftPixelCall } = useDerbySoftPixel();
const router = useRouter();

const { t } = useI18n();

const route = useRoute();
const ridcode = (route.params.ridcode as string).toUpperCase();

const currentFilter = ref<BookingRoomFilter>(BookingRoomFilter.All);

const { adults, childrenAges } = computeAdultsAndChildren(
  bookingStore.remainingGuests[0]?.adults ?? 2,
  bookingStore.remainingGuests[0]?.childrenAges ?? [],
  bookingStore.maxChildrenAge,
);

const roomError = computed({
  get() {
    return props.roomError;
  },
  set(value: boolean) {
    emits('update:room-error', value);
  },
});

const {
  result,
  error,
  loading,
  onResult: onRoomResult,
  onError: onRoomError,
} = useAvailableRoomsQuery(
  {
    adults,
    childrenAges,
    dateIn: bookingStore.dates.dateIn as string,
    hotelId: ridcode,
    nights: bookingStore.dates.nights,
  },
  {
    fetchPolicy: 'no-cache',
  },
);

const sendDerbySoftTracking = () => {
  const cheapestRoom = allRooms.value[0];

  if (!cheapestRoom) {
    return;
  }

  if (!cheapestRoom.availabilityRoom?.offers) {
    return;
  }

  const offers = cheapestRoom.availabilityRoom?.offers;

  if (offers.length === 0) {
    return;
  }

  const cheapestOffer = (offers as RoomOffers[]).find((offer: RoomOffers) => {
    return (offer?.categories as Categories[])?.find(
      (category: Categories) => category.code === BookingRateFilter.Standard,
    );
  });
  const afterTax = cheapestOffer?.pricing?.amount?.afterTax || 0;
  const hotelKeeper = cheapestOffer?.pricing?.amount?.hotelKeeper || 0;

  derbySoftPixelCall('rate_selection', {
    is_landing_page: 'TRUE',
    hotel_id: bookingStore.hotelId || '',
    check_in_date: bookingStore.dates.dateIn || '',
    check_out_date: bookingStore.isoDateOut || '',
    stay_length: bookingStore.dates?.nights?.toString(),
    total_guests: bookingStore.totalGuestsCount.toString(),
    rooms: bookingStore.numberOfRooms.toString(),
    adults: (adults || 2).toString(),
    children:
      bookingStore.remainingGuests[0]?.childrenAges?.length?.toString() || '0',
    room_type_id: cheapestRoom?.accommodationRoom?.id?.toString() || '',
    room_type: cheapestRoom?.accommodationRoom?.label?.toString() || '',
    rate_plan_id: cheapestOffer?.code?.toString() || '',
    rate_plan_name: cheapestOffer?.label?.toString() || '',
    price_base: hotelKeeper.toString(),
    price_tax_fees: (afterTax - hotelKeeper).toFixed(2).toString(),
    price_total: afterTax.toString(),
    price_currency: cheapestOffer?.pricing?.currency ?? '',
  });
};

onMounted(() => {
  const preferentialCode = usePreferentialCode();
  if (preferentialCode) {
    router.push({
      query: {
        ...router.currentRoute.value.query,
        preferentialCode,
      },
      replace: true,
    });
  }
  const cookieBasketId = useCookie(COOKIE_BASKET_ID);

  // Since we come in step 1, we are in a new basket
  // Delete booking keys
  Object.values(SESSION_STORAGE_KEYS).forEach(value => {
    if (value.includes('booking::')) {
      window.sessionStorage.removeItem(value);
    }
  });
  window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.BASKET_AUTH_CREATION);
  cookieBasketId.value = null;
  bookingStore.basketId = null;

  const destinationValue =
    bookingStore.hotelDetails.country + ' - ' + bookingStore.hotelDetails.city;

  const departureDate = new Date(bookingStore.isoDateOut as string);

  event('funnel_start', {
    adults_nb: bookingStore.nbAdults,
    arrival_date: bookingStore.dates.dateIn,
    children_age1: bookingStore.remainingGuests[0]?.childrenAges ?? [],
    children_nb: bookingStore.remainingGuests[0]?.childrenAges?.length ?? 0,
    day_before_arrival: bookingStore.dates?.nights,
    departure_date: departureDate,
    lead_time: bookingStore.dates.dateIn,
    night_nb: bookingStore.dates?.nights,
    room_nb: bookingStore.rooms.length,
    destination: destinationValue,
    destination_city: bookingStore.hotelDetails.city,
    destination_city_code: bookingStore.hotelDetails.cityCode,
    destination_country_code: bookingStore.hotelDetails.cityCode,
    destination_country_name: bookingStore.hotelDetails.country,
    hotel_brand_code: bookingStore.hotelDetails.brandCode,
    hotel_name: bookingStore.hotelDetails.hotelName,
    hotel_rid_code: bookingStore.hotelId,
    accomodation_type: '',
    booking_form_type: '',
    child_conversion_status: '',
    offer_code: '',
    client_type: '',
  });

  ecommerce(
    'begin_checkout',
    {
      value: '',
      children_nb: bookingStore.totalChildrenRemainingCount,
      adults_nb: bookingStore.totalAdultsRemainingCount,
      location_id: bookingStore.hotelDetails.countryCode,
      arrival_date: bookingStore.dates?.dateIn ?? '',
      currency: bookingStore.currentCurrency,
      departure_date: bookingStore.isoDateOut ?? '',
      room_nb: bookingStore.numberOfRooms,
      night_nb: bookingStore.nights,
      items: [
        {
          item_id: bookingStore.hotelId ?? '',
          item_name: bookingStore.hotelDetails.hotelName,
          item_category: 'room',
          item_category2: '',
          item_category3: '',
          item_category4: bookingStore.hotelDetails.cityCode,
          item_brand: bookingStore.hotelDetails.brandCode,
          item_variant: '',
          price: '',
          quantity: bookingStore.numberOfRooms,
          night_nb: bookingStore.dates?.nights,
          location_id: bookingStore.hotelDetails.cityCode,
        },
      ],
    },
    'Ecommerce - Begin Checkout',
  );
});

onRoomError(gqlError => emits('error', gqlError));

onRoomResult(() => {
  if (preferentialCode) {
    const hasOffersWithPromoCode = result.value?.availableRooms?.some(room =>
      room?.availabilityRoom?.offers?.some(
        offer => offer?.code === preferentialCode,
      ),
    );

    bookingStore.setHasOffersWithPromoCode(hasOffersWithPromoCode);
    bookingStore.roomResultTrigger++;
  }

  roomError.value = (result.value?.availableRooms?.length ?? 0) <= 0;

  try {
    sendDerbySoftTracking();
  } catch (e) {
    /* eslint-disable-next-line no-console */
    console.error(e);
  }

  if (
    availableFilters.value.some(
      filter => filter.value === BookingRoomFilter.All,
    )
  )
    return;

  currentFilter.value = availableFilters.value[0].value as BookingRoomFilter;
});

const allRawAvailableRooms = computed(
  () => (result?.value?.availableRooms || []) as AvailableRoom[],
);

const allAvailableRooms = useAvailableRoomsSorted(allRawAvailableRooms);
const allRooms = useFilteredAvailableRoomOffers(allAvailableRooms);

const rooms = computed(() => {
  return allRooms.value.filter(
    room =>
      !room?.accommodationRoom ||
      room?.accommodationRoom?.classification?.type === RoomCategory.Room,
  );
});

const suites = computed(() => {
  return allRooms.value.filter(
    room =>
      room?.accommodationRoom?.classification?.type === RoomCategory.Suite,
  );
});

const villas = computed(() => {
  return allRooms.value.filter(
    room =>
      room?.accommodationRoom?.classification?.type === RoomCategory.Villa,
  );
});

const penthouse = computed(() => {
  return allRooms.value.filter(
    room =>
      room?.accommodationRoom?.classification?.type === RoomCategory.Penthouse,
  );
});

const selectedRooms = computed(() => {
  switch (currentFilter.value) {
    case BookingRoomFilter.Rooms:
      return rooms.value;
    case BookingRoomFilter.Suites:
      return suites.value;
    case BookingRoomFilter.Villas:
      return villas.value;
    case BookingRoomFilter.Penthouse:
      return penthouse.value;
    case BookingRoomFilter.All:
    default:
      return allRooms.value;
  }
});

const availableFilters = computed<BookingFilterValues[]>(() => {
  const roomTitle = t(`booking.title.single.room`);

  const filters = [
    {
      label: t(`booking.filters.labels.all`),
      ariaLabel: roomTitle + ' - ' + t(`booking.filters.labels.all`),
      value: BookingRoomFilter.All,
    },
  ];

  if (rooms.value.length > 0) {
    filters.push({
      label: t(`booking.filters.labels.rooms`),
      ariaLabel: roomTitle + ' - ' + t(`booking.filters.labels.rooms`),
      value: BookingRoomFilter.Rooms,
    });
  }

  if (suites.value.length > 0) {
    filters.push({
      label: t(`booking.filters.labels.suites`),
      ariaLabel: roomTitle + ' - ' + t(`booking.filters.labels.suites`),
      value: BookingRoomFilter.Suites,
    });
  }

  if (villas.value.length > 0) {
    filters.push({
      label: t(`booking.filters.labels.villas`),
      ariaLabel: roomTitle + ' - ' + t(`booking.filters.labels.villas`),
      value: BookingRoomFilter.Villas,
    });
  }

  if (penthouse.value.length > 0) {
    filters.push({
      label: t(`booking.filters.labels.penthouse`),
      ariaLabel: roomTitle + ' - ' + t(`booking.filters.labels.penthouse`),
      value: BookingRoomFilter.Penthouse,
    });
  }

  if (filters.length === 2) {
    // Remove the all as there's only one category
    filters.shift();
  }

  return filters;
});

function setRoom(code: string) {
  bookingStore.updateCurrentRoom({
    productCode: code,
  });

  emits('submit', 'room');
}
</script>
